import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Users, PawPrint, PartyPopper, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";

const questions = [
  {
    id: 'household',
    question: "How busy is your household?",
    icon: <Users className="w-12 h-12 mb-6 text-primary" />,
    options: [
      { label: "Very busy (Kids, chaotic schedules)", score: 3 },
      { label: "Moderate (Working professionals, some activity)", score: 2 },
      { label: "Quiet (Single or couple, rarely home)", score: 1 }
    ]
  },
  {
    id: 'pets',
    question: "Do you have indoor pets?",
    icon: <PawPrint className="w-12 h-12 mb-6 text-primary" />,
    options: [
      { label: "Yes, multiple pets or heavy shedders", score: 3 },
      { label: "Yes, one pet that doesn't shed much", score: 2 },
      { label: "No pets", score: 1 }
    ]
  },
  {
    id: 'hosting',
    question: "How often do you host guests?",
    icon: <PartyPopper className="w-12 h-12 mb-6 text-primary" />,
    options: [
      { label: "Frequently (Weekly or impromptu gatherings)", score: 3 },
      { label: "Occasionally (A few times a month)", score: 2 },
      { label: "Rarely (Only for holidays or special events)", score: 1 }
    ]
  },
  {
    id: 'standards',
    question: "What's your personal cleanliness standard?",
    icon: <Sparkles className="w-12 h-12 mb-6 text-primary" />,
    options: [
      { label: "Immaculate (I notice every speck of dust)", score: 3 },
      { label: "Tidy (I like it clean but can tolerate some mess)", score: 2 },
      { label: "Relaxed (A little mess doesn't bother me)", score: 1 }
    ]
  }
];

export function CleaningFrequencyPlanner() {
  const [currentStep, setCurrentStep] = useState(0);
  const [scores, setScores] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);

  const handleSelect = (score: number) => {
    const newScores = [...scores, score];
    setScores(newScores);

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResult(true);
    }
  };

  const resetQuiz = () => {
    setCurrentStep(0);
    setScores([]);
    setShowResult(false);
  };

  const totalScore = scores.reduce((a, b) => a + b, 0);

  const getRecommendation = () => {
    if (totalScore >= 10) {
      return {
        frequency: "Weekly",
        discount: "35% OFF",
        description: "Your busy household needs consistent upkeep. A weekly clean will keep the chaos at bay, manage pet hair, and ensure your home is always guest-ready.",
        color: "bg-primary text-primary-foreground"
      };
    } else if (totalScore >= 7) {
      return {
        frequency: "Bi-Weekly",
        discount: "30% OFF",
        description: "Our most popular option! Perfect for maintaining a clean home while handling normal day-to-day mess. It strikes the perfect balance of cost and cleanliness.",
        color: "bg-accent text-accent-foreground"
      };
    } else {
      return {
        frequency: "Monthly",
        discount: "20% OFF",
        description: "Since your home stays relatively tidy, a thorough monthly deep clean is perfect to tackle the buildup you don't have time for.",
        color: "bg-secondary text-secondary-foreground"
      };
    }
  };

  if (showResult) {
    const result = getRecommendation();
    return (
      <Card className="w-full max-w-3xl mx-auto shadow-elegant border-primary/20 overflow-hidden anim-fade-in is-visible">
        <div className={`p-10 text-center ${result.color}`}>
          <Calendar className="w-20 h-20 mx-auto mb-6 opacity-90" />
          <h3 className="text-2xl font-bold mb-2">Your Ideal Cleaning Schedule:</h3>
          <div className="text-6xl font-black mb-4">{result.frequency}</div>
          <div className="inline-block bg-white/20 px-6 py-2 rounded-full font-bold tracking-wider mb-2 text-lg">
            SAVES {result.discount}
          </div>
        </div>
        <CardContent className="p-8 md:p-12 text-center space-y-8 bg-card">
          <p className="text-xl text-muted-foreground">{result.description}</p>
          <div className="space-y-4 text-left max-w-lg mx-auto bg-muted p-8 rounded-2xl">
            <h4 className="font-bold text-lg flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-primary" /> Why this works for you:</h4>
            <ul className="space-y-3 text-base text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                Matches your household's activity level
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                Prevents deep buildup from accumulating
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                Maximizes your recurring discount savings
              </li>
            </ul>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <Button variant="outline" size="lg" onClick={resetQuiz} className="h-14 px-8 rounded-full text-lg">Retake Quiz</Button>
            <Button size="lg" className="h-14 px-8 rounded-full text-lg" onClick={() => {
              const planDetails = {
                frequency: result.frequency.toLowerCase().replace("-", ""),
                serviceType: 'standard',
                sqft: 1500,
                addons: [],
                totalPrice: 0 // Will be recalculated by widget
              };
              localStorage.setItem('uplevel_saved_plan', JSON.stringify(planDetails));
              document.dispatchEvent(new Event('open-booking'));
            }}>
              Book {result.frequency} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentQ = questions[currentStep];

  return (
    <Card className="w-full max-w-3xl mx-auto shadow-elegant border-border overflow-hidden">
      <div className="bg-muted/50 p-6 pb-4 border-b">
        <div className="flex justify-between text-sm font-semibold text-muted-foreground mb-3">
          <span>Question {currentStep + 1} of {questions.length}</span>
          <span>{Math.round(((currentStep) / questions.length) * 100)}%</span>
        </div>
        <Progress value={((currentStep) / questions.length) * 100} className="h-2.5" />
      </div>
      <CardContent className="p-8 md:p-12 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6">
          <Sparkles className="w-4 h-4" />
          <span>Cleaning Persona Quiz</span>
        </div>
        <div className="flex justify-center anim-fade-in is-visible" key={`icon-${currentStep}`}>
          {currentQ.icon}
        </div>
        <h3 className="text-3xl md:text-4xl font-bold mb-10 anim-fade-in is-visible" key={`q-${currentStep}`}>{currentQ.question}</h3>
        
        <div className="space-y-4 max-w-xl mx-auto">
          {currentQ.options.map((option, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(option.score)}
              className="w-full p-6 text-left border-2 rounded-2xl hover:border-primary hover:bg-primary/5 transition-all anim-fade-up is-visible flex items-center justify-between group"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <span className="font-semibold text-lg">{option.label}</span>
              <div className="w-6 h-6 rounded-full border-2 border-muted-foreground/30 group-hover:border-primary flex items-center justify-center shrink-0 ml-4">
                <div className="w-3 h-3 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
